import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AngularFirestore, DocumentChangeAction } from "@angular/fire/compat/firestore";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { doc, docData, docSnapshots, DocumentData, DocumentReference, Firestore, getDoc } from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { UserRanks } from "@interfaces/social.i";
import { BehaviorSubject, firstValueFrom, forkJoin, from, Observable, of, throwError } from "rxjs";
import { catchError, delay, first, map, reduce, retryWhen, shareReplay, switchMap, take, tap } from "rxjs/operators";
import { MysteryBoxComponent } from "../components/mystery-box/mystery-box.component";
import { CommonService } from "./common/common.service";
import { PaymentDataService } from "./payment-data.service";
import { UserManagementService } from "./user-management.service";
import { FirestoreDataService } from "./firestore-data.service";
@Injectable({
  providedIn: "root",
})
export class SocialService {
  userDetailsForHome$ = this.userManagementService.userDetailsForHome$;
  public gameMissionData$ = new BehaviorSubject(null);
  public gameFortuneMissionData$ = new BehaviorSubject(null);
  searchTermResponses: { [key: string]: Data } = {};
  leaderBoardData$ = new BehaviorSubject(null);
  private data = new BehaviorSubject<string>("");
  defaultState = {
    community: { followers: 0, friends: 0, likes: 0 },
    fortune: { fantasy_album: 0, missions: 0, wins: 0 },
    gaming: { leaderboard: 0, missions: 0, wins: 0 },
  };
  setData(data: string) {
    this.data.next(data);
  }
  getData() {
    return this.data?.asObservable();
  }
  defaultStateBalance = {
    cxp: 0,
    fxp: 0,
    gxp: 0,
    vxp: 0,
  };
  public userID: string;
  public category: string;
  public subcategory: string;
  public mineImageData: Data;
  public imageData: Data;
  public likedImages: Data;
  public customCheck: boolean;
  socialData$ = this.userManagementService.userRequest$.pipe(
    switchMap((user: Data) => {
      return user && user.uid
        ? this.afs
          .doc(`users/${user.uid}/gamification/social_data`)
          .snapshotChanges()
          .pipe(
            map((res: Data) => (res?.payload?.exists ? res?.payload?.data() : this.defaultState)),
            shareReplay(1)
          )
        : of(this.defaultState);
    })
  );

  socialBalance$ = this.userManagementService.userRequest$.pipe(
    switchMap((user: Data) => {
      if (user?.uid) {
        const balanceRef = doc(this.firestore, `users/${user.uid}/gamification/balance`);
        return docData(balanceRef).pipe(
          map((data: any) => data ?? this.defaultStateBalance),
          shareReplay(1)
        );
      }
      return of(this.defaultStateBalance);
    })
  );
  socialGamesDiscovery$ = this.userManagementService.userRequest$.pipe(
    switchMap((user: Data) => {
      return user && user?.uid
        ? this.afs
          .doc(`users/${user?.uid}/gamification/game_discovery`)
          .snapshotChanges()
          .pipe(
            map(async (res: Data) => {
              const result = {};
              if (res?.payload?.exists) {
                const gameDiscoveryDoc = res?.payload?.data();
                if (!gameDiscoveryDoc["group_not_me"] || !gameDiscoveryDoc["group_not_me"].length) {
                  try {
                    //v2
                    const updateGroupNotMe = this.authService?.getV2CloudFunction(
                      "gamediscoverycfv2",
                      `${user?.uid}`
                    );
                    //v1
                    // const updateGroupNotMe = this.angularFireFunctions.httpsCallable("gameDiscoveryCF")(
                    //   `${user?.uid}`
                    // );
                    updateGroupNotMe.subscribe(() => {
                      // gameDiscoveryDoc['group_not_me'] = response.data;
                    });
                  } catch (error) {
                    console.log("Error", error);
                  }
                }

                Object.keys(gameDiscoveryDoc).map((key) => {
                  if (key === "update_timestamp") return;
                  result[key] = [];
                  if (Array.isArray(gameDiscoveryDoc[key])) {
                    gameDiscoveryDoc[key].map((d) => {
                      if (d && d?.count && d?.game_id && d?.game_id?.id && typeof d?.game_id === "object") {
                        result[key].push({ count: d?.count, gameRef: d?.game_id.get() });
                      }
                      return result;
                    });
                  }
                  return key;
                });
                return result;
              }
            })
          )
        : of(this.defaultStateBalance);
    })
  );

  async getGameTypePromises(gameTypeData) {
    return new Promise((resolve, reject) => {
      if (!gameTypeData) {
        reject(false);
      }
      const allPromises = [];
      gameTypeData?.map((g) => {
        return allPromises?.push(g?.gameRef);
      });
      Promise.all(allPromises)
        .then((allData) => {
          const gameData = allData?.map((a) => a?.data());
          resolve(gameData);
        })
        .catch((error) => {
          return error;
        });
    });
  }

  async getUserGameRef(ref) {
    return new Promise((resolve) => {
      const alls = [];
      if (ref) {
        alls.push(
          ref?.map(async (e) => {
            return e?.reference?.get();
          })
        );
        Promise.all(alls[0])
          .then((alldata) => {
            const gamedata = alldata.map((a) => a?.data());
            resolve(gamedata);
          })
          .catch((error) => {
            return error;
          });
      }
    });
  }

  async socialGamingMission_list() {
    try {
      // ✅ Get user data once
      const userData: Data | null = await firstValueFrom(
        this.userManagementService.userDetailsForHome$
      );

      if (!userData) {
        throw new Error(
          "User data not found while fetching gaming missions."
        );
      }

      let snapshot: any;
      let path: string;

      // ✅ Handle group type: string vs object
      if (typeof userData.peepso_group === "string") {
        path = `/Global_lookup/peepso_user_groups/${userData?.country_name}/${userData?.peepso_group}/missions/gaming_list`;
      } else if (userData?.peepso_group?.path) {
        path = `${userData.peepso_group.path}/missions/gaming_list`;
      } else {
        throw new Error("Invalid peepso_group structure in userData.");
      }

      // ✅ Modular Firestore getDoc
      const ref = doc(this.firestore, path);
      snapshot = await getDoc(ref);

      if (!snapshot.exists()) {
        console.warn("[socialGamingMission_list] No gaming missions found in snapshot.");
        return null;
      }

      // ✅ Process snapshot data
      const data = snapshot.data();
      const gamingListCategories: Record<string, any[]> = {
        past: [],
        current: [],
        upcoming: [],
      };

      for (const key of Object.keys(data)) {
        if (key === "update_timestamp" || key === "creation_timestamp") continue;

        if (data[key]) {
          try {
            // Firestore references inside doc
            const ref = data[key];
            const allData = await this.allPromises(ref, key);
            const gamingList = allData?.gaming_list;

            if (gamingList && gamingListCategories[gamingList]) {
              gamingListCategories[gamingList].push(allData);
            } else {
              console.warn(
                `[socialGamingMission_list] Skipping unknown gaming_list type: ${gamingList}`
              );
            }
          } catch (innerErr) {
            console.error(
              `[socialGamingMission_list] Error processing key "${key}":`,
              innerErr
            );
          }
        }
      }

      // ✅ Flatten categories
      const results = [
        ...gamingListCategories.past,
        ...gamingListCategories.current,
        ...gamingListCategories.upcoming,
      ];

      return results;
    } catch (error) {
      console.error("[socialGamingMission_list] Failed:", error);
      return null; // fallback instead of silent fail
    }
  }


  async getMissionData() {
    const res = await this.socialGamingMission_list();
    if (!res) {
      return false;
    }

    await Promise.all(
      Object.keys(res).map(async (key) => {
        res[key].eventData = await this.getAsyncData(res[key]?.event?.get());
        res[key].userId = await this.getAsyncData(res[key]?.contributors?.get());
        res[key].leaderBoardData = [];
      })
    );

    const leaderBoardData = await this.getUserData(res);

    Object.keys(res).forEach((key, i) => {
      res[key].leaderBoardData = leaderBoardData[i];
    });

    const VM = {
      allEvents: Object.values(res).map((r) => r?.eventData),
      allLeaderBoard: Object.values(res).map((r) => r?.leaderBoardData),
      restData: res,
    };

    return VM;
  }

  async socialFortuneMission_list() {
    try {
      const userData: Data | null = await firstValueFrom(
        this.userManagementService.userDetailsForHome$
      );

      if (!userData) {
        throw new Error("User data not found.");
      }

      let snapshot: any;
      if (typeof userData.peepso_group === "string") {
        const path = `/Global_lookup/peepso_user_groups/${userData.country_name}/${userData.peepso_group}/missions/fortune_list`;
        const ref = doc(this.firestore, path);
        snapshot = await getDoc(ref);
      } else if (userData?.peepso_group?.path) {
        const path = `${userData.peepso_group.path}/missions/fortune_list`;
        const ref = doc(this.firestore, path);
        snapshot = await getDoc(ref);
      } else {
        throw new Error("Invalid peepso_group structure in userData.");
      }

      if (!snapshot.exists()) {
        console.warn("[socialFortuneMission_list] No fortune missions found.");
        return null;
      }

      const data = snapshot.data();
      const fortuneListCategories: Record<string, any[]> = {
        past: [],
        current: [],
        upcoming: [],
      };

      for (const key of Object.keys(data)) {
        if (key === "update_timestamp" || key === "creation_timestamp") continue;

        if (data[key]) {
          try {
            // ✅ data[key] should be a DocumentReference
            const ref = data[key];
            const refSnap = await getDoc(ref);

            if (!refSnap.exists()) {
              console.warn(`[socialFortuneMission_list] Skipping missing doc for key "${key}"`);
              continue;
            }

            const allData = await this.allPromises(refSnap, key);
            const gamingList = allData?.gaming_list;

            if (fortuneListCategories[gamingList]) {
              fortuneListCategories[gamingList].push(allData);
            } else {
              console.warn(`[socialFortuneMission_list] Unknown category: ${gamingList}`);
            }
          } catch (innerErr) {
            console.error(`[socialFortuneMission_list] Error processing key "${key}":`, innerErr);
          }
        }
      }

      const results = [
        ...fortuneListCategories.past,
        ...fortuneListCategories.current,
        ...fortuneListCategories.upcoming,
      ];

      return results;
    } catch (error) {
      console.error("[socialFortuneMission_list] Failed:", error);
      return null;
    }
  }
  async getFortuneMissionData() {
    const res = await this.socialFortuneMission_list();
    if (!res) {
      return false;
    }

    await Promise.all(
      Object.keys(res).map(async (key) => {
        try {
          // event reference
          if (res[key]?.event) {
            const eventRef = res[key].event as DocumentReference<DocumentData>;
            const eventSnap = await getDoc(eventRef);
            res[key].eventData = eventSnap.exists() ? eventSnap.data() : null;
          } else {
            res[key].eventData = null;
          }

          // contributors reference
          if (res[key]?.contributors) {
            const contribRef = res[key].contributors as DocumentReference<DocumentData>;
            const contribSnap = await getDoc(contribRef);
            res[key].userId = contribSnap.exists() ? contribSnap.data() : null;
          } else {
            res[key].userId = null;
          }

          res[key].leaderBoardData = [];
        } catch (err) {
          console.error(`[socialFortuneMission_data] Failed for key "${key}":`, err);
          res[key].eventData = null;
          res[key].userId = null;
          res[key].leaderBoardData = [];
        }
      })
    );

    const leaderBoardData = await this.getUserData(res);

    Object.keys(res).forEach((key, i) => {
      res[key].leaderBoardData = leaderBoardData[i];
    });

    const VM = {
      allEvents: Object.values(res).map((r) => r?.eventData),
      allLeaderBoard: Object.values(res).map((r) => r?.leaderBoardData),
      restData: res,
    };

    return VM;
  }

  async getUserData(users: any[]): Promise<any[]> {
    const promises: Promise<void>[] = [];

    for (const user of users) {
      if (user && user.userId && user?.userId?.top_contributors) {
        const topContributors: any[] = [];
        for (const contributor of user.userId.top_contributors) {
          if (!contributor?.user_id) continue; // skip empty user_id

          const promise = (async () => {
            try {
              const userRef = doc(this.firestore, `users/${contributor.user_id}`);
              const userSnap = await getDoc(userRef);

              if (userSnap.exists()) {
                const userData: any = userSnap.data();
                userData["gxp"] = contributor?.gxp;
                userData["fxp"] = contributor?.fxp;
                userData["wp_userID"] = contributor?.wp_user_id;
                topContributors.push(userData);
              }
            } catch (error) {
              console.error(
                `[getUserData] Error retrieving user data for userId "${contributor?.user_id}":`,
                error
              );
            }
          })();

          promises.push(promise);
        }

        // After contributors are processed, push to leaderboard
        const finalize = (async () => {
          await Promise.all(promises);
          if (topContributors.length > 0) {
            user.leaderBoardData.push(...topContributors);
          }
        })();

        promises.push(finalize);
      }
    }
    await Promise.all(promises);
    return users.map((user) => user?.leaderBoardData ?? []);
  }
  async allPromises(ref, key) {
    try {
      const snapshot = await ref;
      const querySnapshot = snapshot?.data();
      querySnapshot["gaming_list"] = key;
      return querySnapshot;
    } catch (error) {
      return {};
    }
  }
  // Get data from firestore
  getAngularFireStore(url: string, method: string) {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null); // Handle offline case
        }
        const docRef = doc(this.firestore, url); // Firestore document reference
        if (method === "get") {
          // For one-time document fetch
          return from(getDoc(docRef)).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        }
        if (method === "snapshotChanges") {
          // For real-time document updates
          return docSnapshots(docRef).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        }
        return of(null); // Default fallback for unsupported methods
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    );
  }
  async socialFortuneLeaderboardPlayerWeek() {
    try {
      const userData: Data = await this.userManagementService.userDetailsForHome$.pipe(first()).toPromise();
      if (!userData) {
        throw new Error("User data not found.");
      }
      let snapshot;
      if (typeof userData.peepso_group === "string") {
        snapshot = `/Global_lookup/peepso_user_groups/${userData?.country_name}/${userData?.peepso_group}/leaderboards/fortune/leaderboard/players_this_week`;
      } else {
        const peepso_group_url = userData?.peepso_group;
        const path = peepso_group_url["path"];
        snapshot = `${path}/leaderboards/fortune/leaderboard/players_this_week`;
      }
      return new Promise((resolve) => {
        this.authService
          .getAngularFireStore(`${snapshot}`, "snapshotChanges")
          .pipe(
            map(async (gameleaderboard: Data) => {
              if (gameleaderboard?.payload?.exists) {
                const leaderboard = gameleaderboard?.payload?.data();
                let current = await this.getCurrentData(leaderboard);
                leaderboard["current_list"] = current;
                return leaderboard;
              }
              return null;
            }),
            shareReplay(1),
            retryWhen((error) => error.pipe(delay(2000), take(3))),
            catchError((err) => {
              return of([]);
            })
          )
          .subscribe((leaderboard) => {
            resolve(leaderboard);
          });
      });
    } catch (error) {
      return [];
    }
  }

  getAsyncData(ref) {
    return new Promise((resolve, reject) => {
      if (!ref) {
        reject(false);
      }
      if (typeof ref === "string" || typeof ref === undefined) {
        resolve({});
      }
      if (ref) {
        ref
          .then((d) => {
            resolve(d?.data());
          })
          .catch((error) => {
            return error;
          });
      }
    });
  }

  async getUserGameDiscoveryDetails() {
    return new Promise((resolve, reject) => {
      this.socialGamesDiscovery$
        .pipe(first())
        .toPromise()
        .then(async (res) => {
          if (!res || !Object.keys(res).length) {
            reject(false);
          }
          if (res) {
            for (let gameType of Object.keys(res)) {
              const gameData = await this.getGameTypePromises(res[gameType]);
              const allRefData = await this.getUserGameRef(gameData);
              res[gameType].map(async (allGameData, index) => {
                allGameData["gameData"] = gameData[index];
                allGameData["gameData"]["ref"] = allRefData[index];
              });
            }
          }
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getConstantsMsg(data) {
    return this.authService.getConstantsMsg(data);
  }

  showToastMessage(a, b) {
    this.authService.showToastMessage(a, b);
  }

  validateApiPostReq(post) {
    return this.authService.validateApiPostReq(post);
  }

  getVIPbenefits(benefit: string) {
    return new Promise((resolve) => {
      return this.afs
        .doc(`gamification_ranks/vip_status/benefit/${benefit}`)
        .snapshotChanges()
        .pipe(
          map((res: Data) => {
            const result: Data = res?.payload?.exists ? res?.payload?.data() : [];
            if (res?.payload?.exists) {
              result.next
                .get()
                .then((r) => {
                  const nextData = r?.data();
                  result["name"] = benefit;
                  result["nextData"] = nextData;
                  result["nextData"]["name"] = r?.id;
                })
                .catch((error) => {
                  return error;
                });
              resolve(result);
            }
            resolve(result);
            return res;
          })
        )
        .subscribe();
    });
  }
  socialFortuneMap$ = this.userManagementService.userRequest$.pipe(
    switchMap((user: Data) => {
      return user && user?.uid
        ? this.afs
          .doc(`users/${user?.uid}/gamification/fortune_map`)
          .snapshotChanges()
          .pipe(
            map((allnodes: Data) => {
              const data = allnodes?.payload?.data();
              return data;
            })
          )
        : of(this.defaultStateBalance);
    })
  );
  async socialFortuneMapGlobalData() {
    return new Promise((resolve) => {
      const bucketRef = this.afs.collection(`/Global_lookup/fortune_map/lifestyle`);
      const garageRef = this.afs.collection(`/Global_lookup/fortune_map/transport`);
      const holidayRef = this.afs.collection(`/Global_lookup/fortune_map/holiday`);
      const propertyRef = this.afs.collection(`/Global_lookup/fortune_map/real_estate`);
      const fortune_map = this.afs
        .doc(`/Global_lookup/fortune_map`)
        .get()
        .pipe(map((doc: Data) => ({ collection: "fortuneMapUrl", data: doc?.data() })));
      const bucketObservable = bucketRef
        .doc(`assets`)
        .get()
        .pipe(map((doc: Data) => ({ collection: "bucket", data: doc?.data() })));
      const garageObservable = garageRef
        .doc(`assets`)
        .get()
        .pipe(map((doc: Data) => ({ collection: "garage", data: doc?.data() })));
      const holidayObservable = holidayRef
        .doc(`assets`)
        .get()
        .pipe(map((doc: Data) => ({ collection: "holiday", data: doc?.data() })));
      const propertyObservable = propertyRef
        .doc(`assets`)
        .get()
        .pipe(map((doc: Data) => ({ collection: "property", data: doc?.data() })));

      forkJoin([bucketObservable, garageObservable, holidayObservable, propertyObservable, fortune_map])
        .pipe(reduce((acc, curr: any) => [...acc, ...curr], []))
        .subscribe(async (data) => {
          resolve(data);
        });
    });
  }

  async socialFortuneDiscover() {
    const userData: Data = await this.userManagementService.userDetailsForHome$.pipe(first()).toPromise();
    if (!userData) {
      throw new Error("User data not found.");
    }
    let snapshot;
    if (userData) {
      if (typeof userData?.peepso_group === "string") {
        snapshot = `/Global_lookup/peepso_user_groups/${userData?.country_name}/${userData?.peepso_group}/discover/lotteries`;
      } else {
        const peepso_group_url = userData?.peepso_group;
        const path = peepso_group_url["path"];
        snapshot = `${path}/discover/lotteries`;
      }
      const flatArray = { top: [], try: [] };
      return new Promise((resolve) => {
        this.authService
          .getAngularFireStore(`${snapshot}`, "snapshotChanges")
          .pipe(
            take(1),
            map((disoveryLotteryRef: Data) => {
              if (disoveryLotteryRef?.payload?.exists) {
                const disoveryLottery = disoveryLotteryRef?.payload?.data();
                Object.keys(disoveryLottery)
                  .filter((f) => f != "update_timestamp")
                  .map((k) => {
                    disoveryLottery[k]?.map((d) => {
                      if (k === "top_within_group") {
                        flatArray.top.push({ count: d?.count, ref: d?.lottery_name?.get() });
                      } else if (k === "why_not_try") {
                        flatArray.try.push({ ref: d?.get() });
                      }
                    });
                  });
                resolve(flatArray);
              }
            })
          )
          .subscribe();
      });
    }
  }
  socalFortueDiscoveryfriends$ = this.userManagementService.userRequest$.pipe(
    switchMap((user: Data) => {
      return user && user.uid
        ? this.afs
          .doc(`users/${user.uid}/gamification/lottery_discovery`)
          .snapshotChanges()
          .pipe(
            map((res: Data) => {
              const result = {};
              if (res?.payload?.exists) {
                const fortuneFriendsDoc = res?.payload?.data();
                Object.keys(fortuneFriendsDoc).map((key) => {
                  if (key === "last_update") return;
                  result[key] = [];
                  if (Array.isArray(fortuneFriendsDoc[key])) {
                    fortuneFriendsDoc[key].map((d) => {
                      result[key].push({ count: d?.count, gameref: d?.lottery_name.get() });
                      return result;
                    });
                  }
                });
              }
              return result;
            })
          )
        : of(this.defaultStateBalance);
    })
  );
  async socialFortuneDiscoverPromises() {
    return new Promise((resolve) => {
      this.socialFortuneDiscover()
        .then(async (res) => {
          for (let key of Object.keys(res)) {
            for (let name of res[key]) {
              if (key == "top") {
                const data = await this.getJackPotData(name?.ref);
                name["lotteryData"] = await data;
              } else {
                const data = await this.getJackPotData(name?.ref);
                name["lotteryData"] = await data;
              }
            }
          }
          resolve(res);
        })
        .catch((error) => {
          return error;
        });
    });
  }
  async getJackPotData(ref) {
    return new Promise((resolve) => {
      ref
        .then((d) => {
          try {
            const data = d?.data();
            resolve(data);
          } catch (error) {
            return error;
          }
        })
        .catch((error) => {
          return error;
        });
    });
  }
  async socalFortueDiscoveryfriendsPromises() {
    return new Promise((resolve, reject) => {
      this.socalFortueDiscoveryfriends$.subscribe(async (res) => {
        if (!res || !Object.keys(res).length) {
          reject(false);
        }
        for (let friendData of Object.keys(res)) {
          const gameData = await this.getFriendPromises(res["friends"]);
          res["friends"].map((allGameData, index) => {
            allGameData["friends"] = gameData[index];
          });
        }

        resolve(res);
      });
    });
  }
  async getFriendPromises(gameTypeData) {
    return new Promise((resolve) => {
      const allPromises = [];
      gameTypeData.map((g) => {
        return allPromises.push(g?.gameref);
      });
      Promise.all(allPromises).then((allData) => {
        const gameData = allData.map((a) => a?.data());
        resolve(gameData);
      });
    });
  }

  async getCurrentData(user) {
    if (user && typeof user === "object" && user?.current_list) {
      const promises = user.current_list.map(async (current) => {
        const userRef = this.afs.doc(`users/${current?.user_id}`).get().toPromise();
        const userDataSnapshot = await userRef;
        const userDataObject = userDataSnapshot.data();
        if (userDataObject) {
          userDataObject["gxp"] = current?.gxp;
          userDataObject["fxp"] = current?.fxp;
          userDataObject["wp_userID"] = current?.wp_userID;
          return userDataObject;
        }
      });
      return await Promise.all(promises).then((userData) => userData?.filter(Boolean));
    }
    return [];
  }

  async getPreviousData(user) {
    if (user && typeof user === "object" && user?.previous_list) {
      const promises = user.previous_list.map(async (previous) => {
        const userRef = this.afs.doc(`users/${previous?.user_id}`).get().toPromise();
        const userDataSnapshot = await userRef;
        const userDataObject = userDataSnapshot?.data();
        if (userDataObject) {
          userDataObject["gxp"] = previous?.gxp;
          userDataObject["wp_userID"] = previous?.wp_userID;
          return userDataObject;
        }
      });
      return await Promise.all(promises).then((userData) => userData?.filter(Boolean));
    }
    return [];
  }

  async socialGamesLeaderBoardPlayerWeek() {
    try {
      const userData: Data = await this.userManagementService.userDetailsForHome$.pipe(first()).toPromise();
      if (!userData) {
        throw new Error("User data not found.");
      }
      let snapshot;
      if (typeof userData.peepso_group === "string") {
        snapshot = `/Global_lookup/peepso_user_groups/${userData?.country_name}/${userData?.peepso_group}/leaderboards/gaming/leaderboard/top_players_this_week`;
      } else {
        const peepso_group_url = userData?.peepso_group;
        const path = peepso_group_url["path"];
        snapshot = `${path}/leaderboards/gaming/leaderboard/top_players_this_week`;
      }
      return new Promise((resolve) => {
        this.afs
          .doc(`${snapshot}`)
          .snapshotChanges()
          .pipe(
            map(async (gameleaderboard: Data) => {
              if (gameleaderboard?.payload?.exists) {
                const leaderboard = gameleaderboard?.payload?.data();
                let current = await this.getCurrentData(leaderboard);
                let previous = await this.getPreviousData(leaderboard);
                leaderboard["current_list"] = current;
                leaderboard["previous_list"] = previous;
                return leaderboard;
              }
              return null;
            }),
            shareReplay(1),
            retryWhen((error) => error.pipe(delay(2000), take(3))),
            catchError((err) => {
              return of([]);
            })
          )
          .subscribe((leaderboard) => {
            resolve(leaderboard);
          });
      });
    } catch (error) {
      return [];
    }
  }

  async socialGamesLeaderBoardHallOfFame() {
    try {
      const userData: Data = await this.userManagementService.userDetailsForHome$.pipe(first()).toPromise();
      if (!userData) {
        throw new Error("User data not found.");
      }
      let snapshot;
      if (typeof userData.peepso_group === "string") {
        snapshot = `/Global_lookup/peepso_user_groups/${userData?.country_name}/${userData?.peepso_group}/leaderboards/gaming/leaderboard/hall_of_fame`;
      } else {
        const peepso_group_url = userData?.peepso_group;
        const path = peepso_group_url["path"];
        snapshot = `${path}/leaderboards/gaming/leaderboard/hall_of_fame`;
      }
      return new Promise((resolve) => {
        this.afs
          .doc(`${snapshot}`)
          .snapshotChanges()
          .pipe(
            map(async (gameleaderboard: Data) => {
              if (gameleaderboard?.payload?.exists) {
                const leaderboard = gameleaderboard?.payload?.data();
                let current = await this.getCurrentData(leaderboard);
                let previous = await this.getPreviousData(leaderboard);
                leaderboard["current_list"] = current;
                leaderboard["previous_list"] = previous;
                return leaderboard;
              }
              return null;
            }),
            shareReplay(1)
          )
          .subscribe((leaderboard) => {
            resolve(leaderboard);
          });
      });
    } catch (error) {
      return [];
    }
  }

  async GamesLeaderBoardPlayerMonth() {
    try {
      const userData: Data = await this.userManagementService.userDetailsForHome$.pipe(first()).toPromise();
      if (!userData) {
        throw new Error("User data not found.");
      }
      let snapshot;
      if (typeof userData.peepso_group === "string") {
        snapshot = `/Global_lookup/peepso_user_groups/${userData?.country_name}/${userData?.peepso_group}/leaderboards/gaming/leaderboard/top_players_this_month`;
      } else {
        const peepso_group_url = userData?.peepso_group;
        const path = peepso_group_url["path"];
        snapshot = `${path}/leaderboards/gaming/leaderboard/top_players_this_month`;
      }
      return new Promise((resolve) => {
        this.afs
          .doc(`${snapshot}`)
          .snapshotChanges()
          .pipe(
            map(async (gameleaderboard: Data) => {
              if (gameleaderboard?.payload?.exists) {
                const leaderboard = gameleaderboard?.payload?.data();
                let current = await this.getCurrentData(leaderboard);
                let previous = await this.getPreviousData(leaderboard);
                leaderboard["current_list"] = current;
                leaderboard["previous_list"] = previous;
                return leaderboard;
              }
              return null;
            }),
            shareReplay(1),
            retryWhen((error) => error.pipe(delay(2000), take(3))),
            catchError((err) => {
              return of([]);
            })
          )
          .subscribe((leaderboard) => {
            resolve(leaderboard);
          });
      });
    } catch (error) {
      return [];
    }
  }

  getDataLeader$ = this.userManagementService.userRequest$.pipe(
    switchMap(() =>
      this.commonService.getUid("socialService").pipe(
        take(1), // take one uid
        switchMap((userId) => {
          if (!userId || userId.length <= 5) {
            return of(null); // invalid uid
          }

          // Run leaderboard queries in parallel
          return forkJoin([
            this.GamesLeaderBoardPlayerMonth(),
            this.socialGamesLeaderBoardPlayerWeek(),
            this.socialGamesLeaderBoardHallOfFame(),
          ]).pipe(
            tap((results) => {
              this.leaderBoardData$.next(results);
            }),
            map(() => true),
            catchError((err) => {
              console.error("Error fetching leaderboard data:", err);
              return of(null);
            })
          );
        })
      )
    ),
    shareReplay(1)
  );

  unsplashApiKey: string;
  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private afs: AngularFirestore,
    private angularFireFunctions: AngularFireFunctions,
    private http: HttpClient,
    private catchService: CatchErrorService,
    private angularFireStore: AngularFirestore,
    private matDialog: MatDialog,
    private firestoreDataService: FirestoreDataService,
    private userManagementService: UserManagementService,
    private paymentDataService: PaymentDataService,
    private commonService: CommonService
  ) {
    this.getUnsplashAPIKey();
    this.getDataLeader$.subscribe();
    this.gamingMissionData$.subscribe();
    this.getFortuneMissionData$.subscribe();
    this.customCheck = true; // update the custom check
    const searchTerms = [
      "Places",
      "Trips",
      "Vacation Home",
      "Accessories",
      "Designer",
      "Toys",
      "Home",
      "Outdoor",
      "Second home",
      "Bikes",
      "Boats",
      "Cars",
      "Planes",
    ];

    const observables: Observable<Data>[] = searchTerms.map((term) =>
      this.getUnsplashByCollections(term).pipe(
        catchError(() => {
          return of(null);
        })
      )
    );

    // Combine observables and subscribe to the result
    forkJoin(observables).subscribe(
      (responses: Data[]) => {
        searchTerms.forEach((term, index) => {
          this.searchTermResponses[term] = responses[index];
        });
      },
      (error) => {
        return throwError(error);
      }
    );
  }
  getFortuneMissionData$ = this.userManagementService.userRequest$.pipe(
    switchMap(() =>
      this.commonService.getUid("socialService").pipe(
        take(1),
        switchMap((userId) => {
          if (!userId || userId.length <= 5) {
            return of(null); // invalid uid
          }

          return from(this.getFortuneMissionData()).pipe(
            tap((d) => {
              this.gameFortuneMissionData$.next(d);
            }),
            catchError((err) => {
              console.error("Error fetching fortune mission data:", err);
              return of(null);
            })
          );
        })
      )
    ),
    shareReplay(1)
  );


  gamingMissionData$ = this.userManagementService.userRequest$.pipe(
    switchMap(() =>
      this.commonService.getUid("socialService").pipe(
        take(1),
        switchMap((userId) => {
          if (!userId || userId.length <= 5) {
            return of(null); // invalid uid
          }

          return from(this.getMissionData()).pipe(
            tap((d) => {
              this.gameMissionData$.next(d);
            }),
            catchError((err) => {
              console.error("Error fetching mission data:", err);
              return of(null);
            })
          );
        })
      )
    ),
    shareReplay(1)
  );


  // ============ Update Social Data from the gamification / badges collection ============//
  // This method has been used to get all the data from the global gamification_ranks
  async globalGamificationRanksData(userLevels: UserRanks) {
    if (!userLevels) {
      throw new Error("Failed to retrieve user levels data. Check network connection and try again.");
    }

    try {
      const { cxp_level, gxp_level, fxp_level, vip_status } = userLevels;

      // Build document references
      const fortuneRef = doc(this.firestore, `gamification_ranks/fortune/level/${fxp_level}`);
      const vipRef = doc(this.firestore, `gamification_ranks/vip_status/level/${vip_status}`);
      const communityRef = doc(this.firestore, `gamification_ranks/community/level/${cxp_level}`);
      const gamingRef = doc(this.firestore, `gamification_ranks/gaming/level/${gxp_level}`);

      // Fetch all docs in parallel
      const [fortuneSnap, vipSnap, communitySnap, gamingSnap] = await Promise.all([
        getDoc(fortuneRef),
        getDoc(vipRef),
        getDoc(communityRef),
        getDoc(gamingRef),
      ]);

      // Map results into consistent shape
      return [
        { collection: "fortuneLevel", data: fortuneSnap.exists() ? fortuneSnap.data() : null },
        { collection: "vipStatusLevel", data: vipSnap.exists() ? vipSnap.data() : null },
        { collection: "communityLevel", data: communitySnap.exists() ? communitySnap.data() : null },
        { collection: "gamingLevel", data: gamingSnap.exists() ? gamingSnap.data() : null },
      ];
    } catch (error) {
      console.error("Error fetching gamification ranks:", error);
      throw new Error("Unable to fetch gamification ranks data. Please try again later.");
    }
  }
  // This method has been used to get convert currency according to the preferred_ currency.
  getDVCurrency(currencyValue: Data) {
    const isEmpty = (val: Data) => val === undefined || val === null || !(Object.keys(val) || val).length;
    return new Promise((resolve, reject) => {
      if (!isEmpty(currencyValue)) {
        currencyValue = currencyValue.replace(",", "");
        let formatedDVData = currencyValue.replace("[", "").replace("]", "");
        let result = formatedDVData?.split(":");
        let dvKey1 = result[0];
        const dvRoundingCondition = dvKey1 === "currency_beauty";
        let dvKey2 = dvRoundingCondition ? Number(result[1])?.toFixed(2) : result[1];
        let preferredCurrency = "GBP";
        let currencySymbol = "£";
        // This is method has been used to get the beautified currency according to the preferred_ currency.
        this.authService.userDetails$
          .pipe(
            catchError((error) => {
              // Handle Error
              return throwError(error);
            })
          )
          .subscribe((user: Data) => {
            try {
              preferredCurrency = user?.preferred_currency ? user?.preferred_currency : "GBP";
              // This is method has been used to get the currency list according to the preferred_ currency.
              this.authService.getCurrencyList$
                .pipe(
                  catchError((error) => {
                    // Handle Error
                    return throwError(error);
                  })
                )
                .subscribe((data: Data) => {
                  try {
                    const CurrencyDetails = data?.details?.find(
                      (result: Data) => result?.currency_code === preferredCurrency
                    );
                    currencySymbol = CurrencyDetails?.currency_symbol ? CurrencyDetails?.currency_symbol : "£";
                    if (dvKey1 === "currency_beauty") {
                      let getBeautifyObj = {};
                      this.paymentDataService
                        .getBeautifiedCurrency(dvKey2, preferredCurrency)
                        .then((beautifiedAmt) => {
                          if (beautifiedAmt) {
                            getBeautifyObj = currencySymbol + beautifiedAmt;
                          } else {
                            getBeautifyObj = currencySymbol + "0";
                          }
                          resolve(getBeautifyObj);
                        })
                        .catch((err) => {
                          reject(err);
                        });
                    }
                  } catch {
                    return throwError("No Currency Data Found");
                  }
                });
            } catch (error) {
              return throwError(error);
            }
          });
      }
    });
  }

  //using this method we are fetching groupURL for peepso tab
  async getGroup() {
    try {
      const userData: Data = await this.userManagementService.userDetailsForHome$.pipe(first()).toPromise();
      if (!userData) {
        throw new Error("User data not found.");
      }
      if (typeof userData?.peepso_group === "string") {
        const userRef = await this.afs
          .doc(`/Global_lookup/peepso_user_groups/${userData?.country_name}/${userData?.peepso_group}`)
          .get()
          .toPromise();
        return userRef?.data();
      } else {
        const peepso_group_url = userData?.peepso_group;
        const path = peepso_group_url["path"];
        const userRef = await this.afs.doc(`${path}`).get().toPromise();
        return userRef?.data();
      }
    } catch (error) {
      throw `An error occurred while getting user group data: ${error.message}`;
    }
  }
  //This funtion is get the vip status data from the global_lookup collection
  getTooltipVipStatus() {
    try {
      return this.afs
        .doc(`/Global_lookup/onboarding/tooltips/vip_status`)
        .get()
        .pipe(
          map((doc: Data) => {
            const data = doc?.data();
            return data;
          })
        );
    } catch (err) {
      return err;
    }
  }

  // ======================== Fortune LIfe style Document Module ============================
  // ========================= Fortune Lifestyle ===========================================//

  // This method has been used to get album images from the global gamification albums
  socialFortuneAlbums$ = this.userManagementService.userRequest$.pipe(
    switchMap((user: Data) => {
      return user && user?.uid
        ? this.authService.getAngularFireStore(`users/${user?.uid}/gamification/albums`, "snapshotChanges").pipe(
          map((res: Data) => {
            const data = res?.payload?.data();
            const id = res?.payload?.id;
            return { id, data };
          })
        )
        : of(this.defaultStateBalance);
    })
  );
  // This method has been used to get album images from the global gamification albums / real_stat
  socialFortuneLifeStyle$ = this.userManagementService.userRequest$.pipe(
    switchMap((user: Data) => {
      return user && user?.uid
        ? this.catchService.isOnline$
          .pipe(
            switchMap((isOnline) => {
              return (
                isOnline &&
                this.afs
                  .collection(`users/${user?.uid}/gamification/albums/real_estate`)
                  .get()
                  .pipe(this.catchService.retryWhen, this.catchService.catchOfflineError)
              );
            }),
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            shareReplay(1)
          )
          .pipe(
            map((r: Data) => {
              const flatArray = [];
              r.docs
                .filter((f) => f.id)
                .map((allnodes) => {
                  const data = allnodes?.data();
                  data["selectedId"] = allnodes?.id;
                  flatArray.push(data);
                });
              return flatArray;
            })
          )
        : of(this.defaultStateBalance);
    })
  );

  // This method has been used to get Random images from the Unsplash API
  getRandomImage() {
    const URL = `https://api.unsplash.com/photos/random?client_id=${this.unsplashApiKey}&count=30`;
    return this.http.get(URL);
  }
  // This method has been used to get Search  images from the Unsplash Search API
  public searchImage(prams) {
    const URL = `https://api.unsplash.com/search/photos/?client_id=${this.unsplashApiKey}&query=${prams}&per_page=50`;
    return this.http.get(URL);
  }
  // This method has been used to get Search  images from the Unsplash CF API 'getUnsplashByCollectionType'
  getUnsplashByCollections(searchTerms) {
    let searchImg = searchTerms.toLowerCase();
    const data = { subCategory: searchImg };
    // V2
    const getUnsplashImages = this.authService.getV2CloudFunction("getunsplashbycollectiontypev2", data);
    // V1
    /* const getUnsplashImages = this.angularFireFunctions.httpsCallable("getUnsplashByCollectionType")(`${searchImg}`); */
    return getUnsplashImages;
  }

  //  This method has been used to add images to the gamification/albums according to the category
  public addSelectedImage(imageUrlData: Data, liked: boolean) {
    // This variable contain the category and subcategory
    const category = imageUrlData?.category ? imageUrlData?.category : "real_estate";
    const subcategory = imageUrlData?.subcategory?.toLowerCase() ? imageUrlData?.subcategory?.toLowerCase() : "bedroom";
    const isMatched = this.mineImageData?.images.some((item) => item.image_url === imageUrlData?.imageURL);
    let result = {
      image_url: imageUrlData?.imageURL,
      liked: liked ? true : false,
    };
    // This variable contain the count of images and limit of images
    const limit = this.mineImageData?.limit;
    const count = isMatched ? this.mineImageData?.images?.length : this.mineImageData?.images?.length + 1;
    // This condition check if the count of images is less than the limit
    if (!isMatched && count <= limit) {
      this.mineImageData?.images.push(result);
      // This method has been used to add images to the gamification/albums according to the category
      this.afs
        .doc(`users/${this.userID}/gamification/albums/${category}/${subcategory}`)
        .set({ images: this.mineImageData?.images, add_allowed: false, count: count }, { merge: true })
        .catch((err) => {
          return err;
        });
    }
    // This method has been used to Remove image from the Liked Lifestyle document
    if (liked === true && imageUrlData != null && imageUrlData != undefined) {
      this.removeLikedLifestyleImages(imageUrlData);
    }
  }
  // This method has been used to Delete images from the gamification/albums
  deleteImage(imageData: Data) {
    // This variable contain the category , subcategory , length ,limit , count
    let deletedKey = [];
    const category = imageData?.category ? imageData?.category : "real_estate";
    const subcategory = imageData?.subcategory?.toLowerCase() ? imageData?.subcategory?.toLowerCase() : "bedroom";
    const length = this.mineImageData?.images?.length;
    const limit = this.mineImageData?.limit;
    const count = this.mineImageData?.count - 1;
    const isMatched = this.mineImageData?.images.some((item) => item.image_url === imageData?.imageURL);
    // This condition check if image URL is matched
    if (isMatched) {
      deletedKey = this.mineImageData?.images.filter((item) => {
        if (item.image_url !== imageData?.imageURL) {
          return item;
        }
      });
    }
    // This method has been used to Delete images from the gamification/albums
    this.afs
      .doc(`users/${this.userID}/gamification/albums/${category}/${subcategory}`)
      .set({ images: deletedKey, count: count }, { merge: true })
      .catch((err) => {
        return err;
      });
    // This method has been used to add update add_allowed to the gamification/albums
    if (length <= limit) {
      this.afs
        .doc(`users/${this.userID}/gamification/albums/${category}/${subcategory}`)
        .set({ add_allowed: true }, { merge: true })
        .catch((err) => {
          return err;
        });
    }
  }
  // This method has been used to get album images from the global gamification albums according to category
  public getLifestyleAlbum(category: string) {
    let cat = category ? category : "transport";
    return this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          return (
            isOnline &&
            this.afs
              .collection(`users/${this.userID}/gamification/albums/${cat}`)
              .get()
              .pipe(this.catchService.retryWhen, this.catchService.catchOfflineError)
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(
        map((r: Data) => {
          const flatArray = [];
          r.docs
            .filter((f) => f.id)
            .map((allnodes) => {
              const data = allnodes?.data();
              data["selectedId"] = allnodes?.id;
              flatArray.push(data);
            });
          return flatArray;
        })
      );
  }

  // ====================================== Liked lifestyle images ====================================//
  // This method has been used to Get Liked images in the album/liked_lifestyle_images document
  public likedLifestyleImages(category: string) {
    const subcategory = category?.toLowerCase() ? category?.toLowerCase() : "bedroom";
    this.authService
      .getAngularFireStore(
        `users/${this.userID}/gamification/albums/liked_lifestyle_images/${subcategory}`,
        "snapshotChanges"
      )
      .pipe(
        map((res: Data) => {
          this.likedImages = res?.payload?.data();
          res?.payload?.data();
        })
      )
      .subscribe();
  }

  // This method has been used to add Liked images in the album/liked_lifestyle_images document
  public addLikedLifestyleImages(imageData: Data) {
    // This variables contain the  subcategory ,imageURL ,All images data, matched images
    const subcategory = imageData?.subcategory?.toLowerCase();
    const imageURL = imageData?.imageURL;
    // Added the condition for blank  category node in the liked_lifestyle_images document
    const allImages = this.likedImages?.images
      ? this.likedImages?.images
      : this.setLikedAlbumImages(imageURL, subcategory);
    const imagesMatch = allImages?.some((item) => item === imageURL);
    // This condition check if image URL is matched
    if (!imagesMatch || imagesMatch === undefined) {
      allImages.push(imageURL);
      // This method has been used to set Liked images in the album/liked_lifestyle_images/subcategory document
      this.afs
        .doc(`users/${this.userID}/gamification/albums/liked_lifestyle_images/${subcategory}`)
        .set({ images: allImages }, { merge: true })
        .catch((err) => {
          return err;
        });
    }
  }

  // This method has been used to set category node in the liked_lifestyle_images document according to the sub category
  setLikedAlbumImages(imageURL: string, subcategory: string) {
    if (imageURL != null && imageURL != undefined && subcategory != null && subcategory != undefined) {
      const likedImages = [];
      likedImages.push(imageURL);
      this.afs
        .doc(`users/${this.userID}/gamification/albums/liked_lifestyle_images/${subcategory}`)
        .set({ images: likedImages }, { merge: true })
        .catch((err) => {
          throw new Error(`Subcategory not found ${err}`);
        });
    }
  }
  // This method has been use to get the Liked images.
  public getLikedImages(category: string) {
    const subCategory = category?.toLowerCase() ? category?.toLowerCase() : "bedroom";
    // This method has been used to get the Liked images from the album/liked_lifestyle_images
    return this.authService
      .getAngularFireStore(
        `users/${this.userID}/gamification/albums/liked_lifestyle_images/${subCategory}`,
        "snapshotChanges"
      )
      .pipe(
        map((res: Data) => {
          return res?.payload?.data() as Data;
        })
      );
  }
  // This method has been used to remove the Liked images from the album/liked_lifestyle_images

  public removeLikedLifestyleImages(imageData: Data) {
    // This Variables contain the  subcategory ,imageURL ,All images data,Matched images
    let deletedKey = [];
    const subcategory = imageData?.subcategory?.toLowerCase();
    const imageURL = imageData?.imageURL;
    const allImages = this.likedImages?.images;
    const imagesMatch = allImages?.some((item) => item === imageURL);
    // This condition check if image URL is matched
    if (imagesMatch) {
      deletedKey = allImages.filter((item) => {
        if (item !== imageURL) {
          return item;
        }
      });
    } else {
      deletedKey = allImages;
    }
    // This method has been used to set deleted images in the album/liked_lifestyle_images/subcategory
    this.afs
      .doc(`users/${this.userID}/gamification/albums/liked_lifestyle_images/${subcategory}`)
      .set({ images: deletedKey }, { merge: true })
      .catch((err) => {
        return err;
      });
  }

  // ========================================= END ===============================================//

  // ===================================== Liked Images Album ====================================//
  // This method has been use to add Liked images in the album
  public albumLikedImages(imageData: Data, isLiked: boolean) {
    // This variables contain the  subcategory , category ,isLiked matched images
    const category = imageData?.category ? imageData?.category : "real_estate";
    const subcategory = imageData?.subcategory?.toLowerCase() ? imageData?.subcategory?.toLowerCase() : "bedroom";
    const isLikedMatched = this.mineImageData?.images.some((item) => item.image_url === imageData?.imageURL);
    // This condition check if image URL is matched
    if (isLikedMatched) {
      this.mineImageData?.images.filter((item) => {
        if (item.image_url == imageData?.imageURL) {
          item.liked = isLiked;
          return item;
        }
      });
    }
    // This method has been used to set Liked images in the album
    this.afs
      .doc(`users/${this.userID}/gamification/albums/${category}/${subcategory}`)
      .set({ images: this.mineImageData?.images }, { merge: true })
      .catch((err) => {
        return err;
      });
  }

  // ================================ End ==================================================//

  // ===================================== Mine Images Album ===============================//
  // This method has been used to get the Mine images Section

  public getMineSectionImages(category: string, subcategory: string) {
    const categories = category?.toLowerCase() ? category?.toLowerCase() : "real_estate";
    let subcategories = subcategory?.toLowerCase() ? subcategory?.toLowerCase().replace(/ /g, "_") : "bedroom";
    // This method has been used to get the Mine images from users/gamification/albums/categories/subcategories
    return this.authService
      .getAngularFireStore(`users/${this.userID}/gamification/albums/${categories}/${subcategories}`, "snapshotChanges")
      .pipe(
        map((res: Data) => {
          this.mineImageData = res?.payload?.data();
          return res?.payload?.data();
        })
      );
  }

  // This method is used to get the category data from global document
  getLifestyleAlbumCategories() {
    try {
      return from(this.authService.getAngularFireStore(`/Global_lookup/gamification_settings`, "get")).pipe(
        map((doc: Data) => {
          const data = doc?.data();
          return data;
        })
      );
    } catch (err) {
      return err;
    }
  }

  // ============================================== Hero Gift box Scrip =================================//
  // ============================================== Start ===============================================//

  // This method has been used to get loot box data from the globle lookup collection
  getMysteryBoxData(lootBoxType: string) {
    if (lootBoxType !== null && lootBoxType !== undefined) {
      return this.catchService.isOnline$
        .pipe(
          switchMap((isOnline) => {
            return (
              isOnline &&
              this.afs
                .collection("/Global_lookup/lootbox_lookup/lootbox_prize_lookup_ad")
                .doc(lootBoxType)
                .get()
                .pipe(this.catchService.retryWhen, this.catchService.catchOfflineError)
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        )
        .pipe(map((doc: Data) => ({ collection: "gamingLevel", data: doc.data() })));
    } else {
      return throwError("Something went wrong");
    }
  }

  // This method has been used to get the claim box data from the global_lookup collection
  getClaimBoxData(lootBoxType: string) {
    if (lootBoxType !== null && lootBoxType !== undefined) {
      return this.catchService.isOnline$
        .pipe(
          switchMap((isOnline) => {
            return (
              isOnline &&
              this.afs
                .collection("Global_lookup/lootbox_lookup/lootbox_prize_lookup_claim")
                .doc(lootBoxType)
                .get()
                .pipe(this.catchService.retryWhen, this.catchService.catchOfflineError)
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        )
        .pipe(map((doc: Data) => ({ collection: "gamingLevel", data: doc?.data() })));
    } else {
      return throwError("Something went wrong");
    }
  }
  // This method has been used to get data form the snapShotData
  private snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]): Data[] {
    // Add type guard to ensure actions is an array before mapping
    if (!Array.isArray(actions)) {
      this.authService.errorHandlerLogs('snapShotData: Input is not an array, returning empty array', { inputType: typeof actions });
      return [];
    }
    
    try {
      return actions.map((a: Data) => {
        try {
          const data = a?.payload.doc?.data() as Data;
          const id = a?.payload.doc.id;
          return { id, ...data };
        } catch (e) {
          return e;
        }
      });
    } catch (error) {
      console.error('snapShotData: Error processing snapshot data', error);
      return [];
    }
  }
  // This method has been used to get the current gift box Data
  getCurrentGiftBox$ = this.catchService.isOnline$
    .pipe(
      switchMap((isOnline) => {
        return (
          isOnline &&
          this.afs
            .collection(`/users/${this.userID}/gamification/giftbox/current`)
            .snapshotChanges()
            .pipe(this.catchService.retryWhen, this.catchService.catchOfflineError)
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,

      shareReplay(1)
    )
    .pipe(
      map((actions: DocumentChangeAction<unknown>[]) => {
        try {
          let data = this.snapShotData(actions);
          // This method has been used to filter the data based on the hero type
          const filterPrizeData = (heroType: string) =>
            data.filter((d) => d?.hero_type === heroType && d?.issued === false && d?.settings?.autoshow === false);
          const gxpPrizeData = filterPrizeData("gxp_level");
          const fxpPrizeData = filterPrizeData("fxp_level");
          const cxpPrizeData = filterPrizeData("cxp_level");
          const autoShowGiftBoxData = data.filter(
            (d) => d?.issued === false && d?.settings?.autoshow === true && d?.settings?.shown_status === false
          );
          // This variable has been used to store the data in the gift box
          const giftBoxData = {
            gxpPrizes: gxpPrizeData,
            fxpPrizes: fxpPrizeData,
            cxpPrizes: cxpPrizeData,
            autoShowGiftBox: autoShowGiftBoxData,
          };
          return giftBoxData;
        } catch (error) {
          return error;
        }
      }),
      shareReplay(1)
    );

  // This method has been used to update the auto gift box shown status = true
  updateAutoShownGiftBoxStatus(lootBoxID: string) {
    this.afs
      .doc(`users/${this.userID}/gamification/giftbox/current/${lootBoxID}`)
      .set({ settings: { shown_status: true } }, { merge: true })
      .catch((err) => {
        return err;
      });
  }
  // =========================== Auto show Gift Box Scripts ======================//
  //This method is used to get claim box data
  getClaimLootBoxData(lootBoxType: string, prizeNumber: number, documentID: string, autoShow: boolean) {
    this.getClaimBoxData(lootBoxType).subscribe((res: Data) => {
      try {
        const dataRes = res;
        const prize = dataRes?.data?.prize_list[prizeNumber].prize;
        const autoClaimLootBoxData = {
          lootBoxType: lootBoxType,
          prize: prize,
          docID: documentID,
          autoShow: autoShow,
        };

        if (this.customCheck) {
          this.customCheck = false;
          const bottomSheetClose = this.matDialog.open(MysteryBoxComponent, {
            panelClass: "mysterybox-dialog-size",
            backdropClass: "mysterybox-back-drop-color",
            hasBackdrop: true,
            disableClose: true,
            data: autoClaimLootBoxData,
          });
          // This method is used to close the dialog
          bottomSheetClose.afterClosed().subscribe((res: string) => {
            if (res === "isClaim") this.tostMassages();
            this.customCheck = true;
          });
        }
      } catch (error) {
        throw new Error(`claim Box data was not found : ${error}`);
      }
    });
  }
  // This method has been used to  open tost messages
  tostMassages() {
    this.authService?.getConstantsMsg("1124").then((translatedMsg) => {
      const defaultMsg = {
        message: {
          line_1: "The reward has been added in your account.",
          line_2: "",
        },
        style: {
          icon: "tick circle success.png",
          type: "Success",
        },
      };
      this.authService.showToastMessage(translatedMsg, defaultMsg);
    });
  }

  async getUnsplashAPIKey() {
    this.firestoreDataService.getV2CloudFunction("readfirestoredoc", {
      path_id: "DsHjjb5zZPACtZES8SIB",
      method: "read",
    })
    .pipe(
      map((response: any) => response?.data?.unsplash_api_key),
      shareReplay({ bufferSize: 1, refCount: true })
    )
    .subscribe((secretKey: string) => {
      this.unsplashApiKey = secretKey;
    });
  }
}
